import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MonitorUp, Square, Loader2, Sparkles, AlertTriangle, RefreshCw, User, Headphones, WifiOff, Mic, MonitorSpeaker, PictureInPicture2, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useLiveSession } from "../hooks/useLiveSession";
import { CategoryBadge } from "../components/CategoryBadge";
import { FloatingCoach, CoachProgress, CoachListening, CoachAdvice, useFloatingCoach, supportsPip } from "../components/FloatingCoach";
import SessionReport from "./SessionReport";

const speakerLabel = (sp, seller) => {
  if (sp == null) return "Voce incerta";
  if (seller == null) return `Speaker ${sp}`;
  return sp === seller ? "Io" : "Cliente";
};

export const groupTranscriptSegments = (segments) => segments.reduce((groups, segment) => {
  const group = groups[groups.length - 1];
  const prev = group?.segments[group.segments.length - 1];
  const a = prev?.metriche, b = segment.metriche;
  const sameStream = !a?.stream_id || !b?.stream_id || a.stream_id === b.stream_id;
  const gap = sameStream && a?.audio_end != null && b?.audio_start != null
    ? b.audio_start - a.audio_end
    : (new Date(segment.timestamp) - new Date(prev?.timestamp)) / 1000;
  if (group && segment.speaker != null && group.speaker === segment.speaker && sameStream && gap >= -0.5 && gap <= 4) {
    group.segments.push(segment);
  } else {
    groups.push({ id: segment.id, speaker: segment.speaker, segments: [segment] });
  }
  return groups;
}, []);


export default function LiveSession() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [ending, setEnding] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    api.get(`/live/sessions/${id}`).then((r) => setSession(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [id]);

  if (!session) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 spin-slow" /> Caricamento…</div>;
  if (session.stato === "terminata") return <SessionReport session={session} />;
  return <ActiveSession session={session} elapsed={elapsed} setElapsed={setElapsed} ending={ending} setEnding={setEnding} onEnded={setSession} />;
}

const ActiveSession = ({ session, elapsed, setElapsed, ending, setEnding, onEnded }) => {
  const live = useLiveSession(session.id, session);
  const coach = useFloatingCoach();
  const transcriptRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - new Date(session.started_at).getTime()) / 1000)), 1000);
    return () => clearInterval(t);
  }, [session.started_at, setElapsed]);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: "smooth" });
  }, [live.segments, live.interim]);

  const endSession = async () => {
    setEnding(true);
    coach.close();
    live.stop();
    try {
      await api.post(`/live/sessions/${session.id}/end`);
      const { data } = await api.get(`/live/sessions/${session.id}`);
      toast.success("Sessione terminata");
      onEnded(data);
    } catch (e) {
      toast.error(errorMessage(e));
      setEnding(false);
    }
  };

  const current = live.suggestions[live.suggestions.length - 1];
  const previous = live.suggestions.slice(0, -1).filter((s) => s.testo_suggerimento).slice(-2).reverse();
  const transcriptTurns = useMemo(() => groupTranscriptSegments(live.segments), [live.segments]);
  const lastTurn = transcriptTurns[transcriptTurns.length - 1];
  const lastPart = lastTurn?.segments[lastTurn.segments.length - 1];
  const attachedChannel = live.multichannel && lastPart?.metriche?.canale != null && lastTurn?.speaker != null
    && (lastPart.metriche.canale === 0 ? lastTurn.speaker === live.sellerSpeaker : lastTurn.speaker !== live.sellerSpeaker)
    ? String(lastPart.metriche.canale) : null;
  const speakers = [...new Set(live.segments.map((s) => s.speaker).filter((s) => s != null))];
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div data-testid="live-session-page" className="space-y-6">
      <div className="fade-up flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/live" data-testid="live-back-link" className="rounded-full border border-border p-2 text-muted-foreground hover:text-white"><ArrowLeft className="h-4 w-4" /></Link>
          <div>
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{session.titolo || "Sessione live"}</h1>
            <div className="mt-1 flex items-center gap-3 text-xs font-mono text-muted-foreground">
              <StatusPill status={live.status} />
              <span data-testid="session-timer">{mm}:{ss}</span>
              {live.status === "listening" && (live.multichannel ? <DualLevel levels={live.channelLevels} /> : <LevelMeter level={live.level} />)}
              {live.status === "listening" && <SourceChips sources={live.sources} />}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {live.status === "listening" && !coach.isOpen && (
            <button data-testid="open-floating-coach-btn" onClick={coach.open} title={supportsPip() ? "Apri la finestra flottante sempre in primo piano" : "Apri il pannello coach"} className="inline-flex items-center gap-2 rounded-full border border-[#F5B942]/40 px-4 py-2.5 text-sm font-medium text-[#FCD990] hover:bg-[#F5B942]/10"><PictureInPicture2 className="h-4 w-4" /> <span className="hidden sm:inline">Finestra</span> coach</button>
          )}
          {live.status === "disconnected" && live.isCapturing && (
            <button data-testid="reconnect-btn" onClick={live.reconnect} className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-4 py-2.5 text-sm font-medium text-amber-300 hover:bg-amber-500/10"><RefreshCw className="h-4 w-4" /> Riconnetti</button>
          )}
          <button data-testid="end-session-btn" onClick={endSession} disabled={ending} className="inline-flex items-center gap-2 rounded-full bg-red-500/90 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-50">
            {ending ? <Loader2 className="h-4 w-4 spin-slow" /> : <Square className="h-4 w-4" />} Termina sessione
          </button>
        </div>
      </div>

      {live.error && (
        <div data-testid="live-error" className="fade-up flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /><p className="text-amber-100">{live.error}</p>
        </div>
      )}

      {live.status === "idle" || live.status === "ended" ? (
        <div data-testid="capture-prompt" className="fade-up surface flex flex-col items-center gap-5 p-10 text-center sm:p-14">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/20 text-primary"><MonitorUp className="h-8 w-8" /></div>
          <div>
            <h2 className="text-lg font-semibold md:text-xl">Ascolta la call: scheda + microfono</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Il browser ti chiederà quale scheda condividere (scegli quella della call e spunta <strong className="text-foreground">"Condividi audio della scheda"</strong>) e poi il permesso per il <strong className="text-foreground">microfono</strong>, così trascrivo sia il cliente sia la tua voce. Il video non viene usato.</p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <button data-testid="start-capture-btn" onClick={() => live.start("auto")} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.4)] transition-all hover:-translate-y-0.5 hover:bg-[#1D4ED8]">
              <Headphones className="h-4 w-4" /> {live.status === "ended" ? "Riprendi ascolto" : "Inizia ad ascoltare"}
            </button>
            <button data-testid="start-mic-only-btn" onClick={() => live.start("mic")} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-white">
              <Mic className="h-4 w-4" /> Solo microfono
            </button>
          </div>
          <p className="max-w-md text-xs text-muted-foreground">"Solo microfono" è utile da smartphone o per una call in vivavoce: il telefono ascolta tutta la conversazione dall'ambiente.</p>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-5">
        <section className="fade-up order-1 min-w-0 lg:order-2 lg:col-span-2">
          <div className="lg:sticky lg:top-6">
            <div className="mb-3 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-[#F5B942]" /> Suggerimento</div>
            <SuggestionCard suggestion={current} pending={live.pendingTrigger} draft={live.draft} coachState={live.coachState} listening={live.status === "listening"} />
            {previous.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Precedenti</p>
                {previous.map((s) => (
                  <div key={s.id} data-testid={`prev-suggestion-${s.id}`} className="surface p-3 text-xs leading-relaxed text-muted-foreground">{s.testo_suggerimento}</div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="fade-up order-2 min-w-0 lg:order-1 lg:col-span-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Trascrizione live</div>
            {live.multichannel && (
              <div data-testid="channel-mode-note" className="flex items-center gap-2 text-xs">
                <span className="inline-flex flex-wrap items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300"><Fingerprint className="h-3 w-3" /> Canali separati: mic / cliente{live.voiceProfile ? " · controllo impronta e duplicati attivo" : " · usa le cuffie per evitare rientri"}</span>
              </div>
            )}
            {speakers.length > 0 && !live.multichannel && (
              <div data-testid="speaker-mapper" className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">La mia voce:</span>
                {live.sellerAuto && live.sellerSpeaker != null && (
                  <span data-testid="seller-auto-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300"><Fingerprint className="h-3 w-3" /> riconosciuta{live.sellerAuto.confidenza ? ` ${live.sellerAuto.confidenza}%` : ""}</span>
                )}
                {live.voiceProfile && live.sellerSpeaker == null && (
                  <span data-testid="seller-auto-pending" className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground"><Fingerprint className="h-3 w-3" /> riconoscimento in corso…</span>
                )}
                {live.voiceProfile ? (
                  <button data-testid="swap-seller-btn" onClick={() => live.markSeller(live.sellerSpeaker === 1 ? 0 : 1)} className="rounded-full border border-border px-2.5 py-1 font-mono text-muted-foreground transition-colors hover:text-white" title="Se il riconoscimento ha invertito le voci, scambia Io/Cliente">
                    Inverti Io/Cliente
                  </button>
                ) : speakers.map((sp) => (
                  <button key={sp} data-testid={`mark-seller-${sp}`} onClick={() => live.markSeller(live.sellerSpeaker === sp ? null : sp)} className={`rounded-full border px-2.5 py-1 font-mono transition-colors ${live.sellerSpeaker === sp ? "border-primary bg-primary/20 text-white" : "border-border text-muted-foreground hover:text-white"}`}>
                    Speaker {sp}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div ref={transcriptRef} data-testid="transcript-panel" className="surface h-[46vh] space-y-3 overflow-y-auto p-4 sm:p-5 lg:h-[62vh]">
            {live.segments.length === 0 && !live.interim && (
              <p className="text-sm text-muted-foreground">{live.status === "listening" ? "In ascolto… il parlato apparirà qui." : "Avvia la cattura per vedere la trascrizione."}</p>
            )}
            {transcriptTurns.map((turn, index) => {
              const isMe = live.sellerSpeaker != null && turn.speaker === live.sellerSpeaker;
              const activeText = index === transcriptTurns.length - 1 && attachedChannel != null ? live.interims[attachedChannel] : "";
              return (
                <div key={turn.id} data-testid={`transcript-turn-${turn.id}`} className={`rounded-lg border-l-2 px-4 py-3 ${isMe ? "border-border bg-secondary/30" : "border-primary/50 bg-primary/5"}`}>
                  <span data-testid={`segment-speaker-${turn.id}`} className={`mb-2 inline-flex items-center gap-1.5 text-[11px] font-mono ${isMe ? "text-muted-foreground" : "text-[#93C5FD]"}`}>
                    <User className="h-3 w-3" />{speakerLabel(turn.speaker, live.sellerSpeaker)}
                    {activeText && <span data-testid={`turn-active-${turn.id}`} className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" aria-label="Sta parlando" />}
                  </span>
                  <p className="break-words text-sm leading-relaxed">
                    {turn.segments.map((part, i) => <span key={part.id} data-testid={`segment-${part.id}`}>{i > 0 ? " " : ""}{part.testo}</span>)}
                    {activeText && <span data-testid={`interim-channel-${attachedChannel}`} className="italic text-muted-foreground"> {activeText}</span>}
                  </p>
                </div>
              );
            })}
            {live.interim && (
              <div data-testid="interim-text" className="space-y-2">
                {Object.entries(live.interims).filter(([channel, text]) => text && channel !== attachedChannel).map(([channel, text]) => (
                  <div key={channel} data-testid={`interim-channel-${channel}`} className="flex gap-3">
                    <span className="mt-0.5 inline-flex h-6 shrink-0 items-center rounded-full bg-secondary px-2 text-[11px] font-mono text-muted-foreground">{live.multichannel ? (channel === "0" ? "Mic…" : "Cliente…") : "…"}</span>
                    <p className="text-sm italic leading-relaxed text-muted-foreground">{text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
      <FloatingCoach coach={coach} suggestion={current} pending={live.pendingTrigger} draft={live.draft} coachState={live.coachState} listening={live.status === "listening"} />
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    idle: ["#9CA3AF", "pronto"],
    connecting: ["#60A5FA", "connessione…"],
    listening: ["#34D399", "in ascolto"],
    disconnected: ["#F87171", "disconnesso"],
    ended: ["#9CA3AF", "in pausa"],
  };
  const [color, label] = map[status] || map.idle;
  return (
    <span data-testid="live-status" className="inline-flex items-center gap-1.5" style={{ color }}>
      {status === "disconnected" ? <WifiOff className="h-3 w-3" /> : <span className={`h-2 w-2 rounded-full ${status === "listening" ? "pulse-dot" : ""}`} style={{ background: color }} />}
      {label}
    </span>
  );
};

const SourceChips = ({ sources }) => (
  <span data-testid="audio-sources" className="inline-flex items-center gap-1.5">
    <span data-testid="source-tab" title={sources.tab ? "Audio scheda attivo" : "Audio scheda non attivo"} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] ${sources.tab ? "bg-emerald-500/15 text-emerald-300" : "bg-secondary text-muted-foreground line-through"}`}><MonitorSpeaker className="h-3 w-3" />scheda</span>
    <span data-testid="source-mic" title={sources.mic ? "Microfono attivo" : "Microfono non attivo"} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] ${sources.mic ? "bg-emerald-500/15 text-emerald-300" : "bg-secondary text-muted-foreground line-through"}`}><Mic className="h-3 w-3" />mic</span>
  </span>
);

const LevelMeter = ({ level }) => (
  <span data-testid="audio-level" className="inline-flex h-3 items-end gap-0.5" title="Livello audio">
    {[0.02, 0.05, 0.1, 0.18, 0.3].map((th, i) => (
      <span key={i} className="w-1 rounded-sm transition-colors" style={{ height: `${(i + 1) * 20}%`, background: level > th ? "#34D399" : "#374151" }} />
    ))}
  </span>
);

const DualLevel = ({ levels }) => (
  <span data-testid="dual-level" className="inline-flex items-center gap-3">
    <span className="inline-flex items-center gap-1"><span className="text-[10px] uppercase text-muted-foreground">Tu</span><LevelMeter level={levels?.mic || 0} /></span>
    <span data-testid="client-level" className="inline-flex items-center gap-1"><span className="text-[10px] uppercase text-[#93C5FD]">Cliente</span><LevelMeter level={levels?.tab || 0} /></span>
  </span>
);

const SuggestionCard = ({ suggestion, pending, draft, coachState, listening }) => {
  // Il consiglio resta a schermo finché il nuovo non inizia ad arrivare (fine frase cliente).
  if (draft) {
    return (
      <div data-testid="suggestion-pending" className="rounded-2xl border border-[#F5B942]/40 bg-[#F5B942]/10 p-6">
        <CoachProgress draft={draft} testPrefix="suggestion" />
      </div>
    );
  }
  if (!suggestion) {
    if (pending) {
      return (
        <div data-testid="suggestion-pending" className="rounded-2xl border border-[#F5B942]/40 bg-[#F5B942]/10 p-6">
          <CoachProgress draft={draft} testPrefix="suggestion" />
        </div>
      );
    }
    if (listening && coachState !== "ready") {
      return <div className="surface p-6"><CoachListening testPrefix="suggestion" /></div>;
    }
    return (
      <div data-testid="suggestion-empty" className="surface p-6 text-sm text-muted-foreground">
        {listening ? "Appena il cliente dice qualcosa di rilevante, qui compare il suggerimento." : "Nessun suggerimento ancora."}
      </div>
    );
  }
  if (!suggestion.testo_suggerimento) {
    return (
      <div data-testid="suggestion-none" className="surface p-6">
        <p className="text-sm font-medium text-muted-foreground">Nessun suggerimento pertinente nei tuoi contenuti.</p>
        <p className="mt-2 text-xs italic text-muted-foreground">“{suggestion.trigger_text}”</p>
        <p className="mt-3 text-xs text-muted-foreground">Aggiungi materiale su questo tema nell'Admin per ricevere suggerimenti.</p>
      </div>
    );
  }
  return (
    <div key={suggestion.id} data-testid="suggestion-current" className={`fade-up coach-ring ${suggestion.azione === "rispondi" ? "coach-ring--red" : "coach-ring--green"}`}>
      <div className="coach-inner p-6">
      <span data-testid="suggestion-action" className={`mb-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${suggestion.azione === "rispondi" ? "bg-red-500/15 text-red-200" : "bg-emerald-500/15 text-emerald-200"}`}>
        {suggestion.azione === "rispondi" ? "Puoi intervenire" : "Lascia spazio"}{suggestion.motivo_azione && <span className="font-normal normal-case tracking-normal opacity-80">· {suggestion.motivo_azione}</span>}
      </span>
      <CoachAdvice suggestion={suggestion} testPrefix="suggestion" />
      <p className="mt-4 text-xs italic text-[#FCD990]/80">In risposta a: “{suggestion.trigger_text}”</p>
      <div className="mt-4 border-t border-white/10 pt-3">
        <p data-testid="suggestion-latency" className="text-[11px] font-mono text-muted-foreground">{suggestion.first_text_ms != null ? `Primo testo ${suggestion.first_text_ms} ms · ` : ""}Risposta completa {suggestion.latency_ms} ms dalla frase trascritta</p>
        <p className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground">Basato su</p>
        <ul className="mt-2 space-y-2">
          {suggestion.knowledge_items_usati.map((k) => (
            <li key={k.id} data-testid={`suggestion-source-${k.id}`} className="flex items-start gap-2 text-xs text-gray-300">
              <CategoryBadge categoria={k.categoria} />
              <span className="line-clamp-2">{k.testo}</span>
            </li>
          ))}
        </ul>
      </div>
      </div>
    </div>
  );
};
